import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { ModalsService } from '@core/modals/modals.service';
import { OktaIntegrationService } from '@core/okta-integration/services/okta-integration.service';
import {
  EVENT_DATE_TBD_TEXT,
  SHOW_EVENT_DATE_AS_TBD,
} from '@core/schedule-tbd/schedule-tbd';
import { DvmService, TutorialService } from '@core/services';
import { AnalyticsService } from '@core/services/analytics';
import { TransactionService } from '@core/services/transaction.service';
import { PrivateSellingService } from '@core/services/private-selling.service';
import { IsolatedSeatsService } from '@features/digital-venue/services/isolated-seats.service';
import { ConnectionService } from '@features/pages/select-seat/services/connection.service';
import { UpgradesComponent } from '@features/pages/select-seat/viewer/topbar/upgrades/upgrades.component';
import { UpgradesService } from '@features/pages/select-seat/viewer/topbar/upgrades/upgrades.service';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import { SaleEvent } from '../../models';
import { ConfigurationService } from '../../services/configuration.service';
import { KNOWN_ROUTE_SEGMENTS } from '../../../app-routing.module';
import { buildLegendContent } from './legend-content';

@Component({
  imports: [CommonModule, NgOptimizedImage, UpgradesComponent],
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  // Booleans
  landing: boolean;
  showLegend: boolean;
  showTutorialButton = false;

  // Strings
  actualPage: string | undefined;
  // 'summary' is the final confirmation page (no further review step to go back to), so it never
  // gets a back button — only 'checkout' does.
  backBtn: 'checkout' | undefined;
  patronName: string | undefined;
  /**
   * Second line of the centred "Welcome, {name}" block (events header, node
   * 1490:4). Populated from the same `OktaIntegrationService.getUserInfo()`
   * call as `patronName` — no separate lookup, and left `undefined` (not a
   * fabricated placeholder) whenever Okta doesn't return one.
   */
  patronEmail: string | undefined;

  /**
   * Signed-in patron block ("Hi, {name}" + Sign out) shown in the header on
   * checkout/summary. Only checkout has an Okta login concept today (the inline
   * `app-okta-container` "Login to use account credit" widget), so this
   * mirrors that same OktaIntegrationService state instead of inventing a
   * separate one; it stays hidden for guests (no Okta session) rather than
   * showing fabricated data.
   */
  showUserInfo = false;
  isAuthenticated = false;

  // Colors
  availableColor: string;

  // Event
  event: SaleEvent;
  readonly showDateAsTbd = SHOW_EVENT_DATE_AS_TBD;
  readonly eventDateTbdText = EVENT_DATE_TBD_TEXT;

  // Subscriptions
  private subscriptions: Subscription[] = [];

  private readonly DATE_FORMAT = 'EEEE, LLL d, y, h:mm a';
  // Split out for the seat-selection header, which
  // interleaves a clock glyph between the weekday and the time — a single
  // preformatted string can't express that, so the date is rendered as
  // three separate parts instead. `DATE_FORMAT`/`formattedEventDate` above
  // are kept as-is for the `title` tooltip.
  private readonly WEEKDAY_FORMAT = 'EEEE';
  private readonly TIME_FORMAT = 'h:mm a';
  private readonly MONTH_DAY_FORMAT = 'LLL d';

  router = inject(Router);
  isolatedSeatsService = inject(IsolatedSeatsService);
  configurationService = inject(ConfigurationService);
  // Gates `<app-upgrades>` below: it should only mount when the current
  // event actually has upgrades to show.
  protected upgradesService: UpgradesService = inject(UpgradesService);
  private connectionService = inject(ConnectionService);
  private dvmService = inject(DvmService);
  private modalsService = inject(ModalsService);
  private tutorialService = inject(TutorialService);
  private transactionService = inject(TransactionService);
  private oktaIntegrationService = inject(OktaIntegrationService);
  private privateSellingService = inject(PrivateSellingService);
  private analyticsService = inject(AnalyticsService);

  // Getters
  get isTopview() {
    return this.dvmService.isTopView();
  }
  get is3dOpen() {
    return this.dvmService.is3dView();
  }
  get isIsolatedActive() {
    return this.isolatedSeatsService.isolatedSeatsActive;
  }
  get eventDateTime(): DateTime | null {
    if (!this.event?.date) {
      return null;
    }
    const hasTimezone =
      this.event.date.includes('Z') || /[+-]\d{2}:\d{2}$/.test(this.event.date);
    if (hasTimezone) {
      return DateTime.fromISO(this.event.date);
    }
    return DateTime.fromISO(this.event.date, { zone: 'utc' });
  }
  get eventTimeZone(): string {
    const eventDt = this.eventDateTime;
    if (!eventDt || !eventDt.isValid) {
      return '';
    }
    return eventDt.offsetNameShort || '';
  }
  get formattedEventDate(): string {
    const eventDt = this.eventDateTime;
    if (!eventDt || !eventDt.isValid) {
      return '';
    }
    return eventDt.setLocale('en').toFormat(this.DATE_FORMAT);
  }
  // Weekday / time / month-day parts of the same event date, split out for
  // the seat-selection header layout. Same guard as `formattedEventDate`.
  get eventWeekday(): string {
    const eventDt = this.eventDateTime;
    if (!eventDt || !eventDt.isValid) {
      return '';
    }
    return eventDt.setLocale('en').toFormat(this.WEEKDAY_FORMAT);
  }
  get eventTimeOfDay(): string {
    const eventDt = this.eventDateTime;
    if (!eventDt || !eventDt.isValid) {
      return '';
    }
    return eventDt.setLocale('en').toFormat(this.TIME_FORMAT);
  }
  get eventMonthDay(): string {
    const eventDt = this.eventDateTime;
    if (!eventDt || !eventDt.isValid) {
      return '';
    }
    return eventDt.setLocale('en').toFormat(this.MONTH_DAY_FORMAT);
  }
  /**
   * Gates the info icon + Sign out pill on the `events` header: shown only
   * when the visit is a group link that requires private selling.
   * `PrivateSellingService.isEnabled` is set
   * by `EventDataResolver` whenever the *current* seat-selection event has
   * `isPrivateSellingEnabled` — it's an app-wide signal (not reset back to
   * `false` on navigation), so it also reads correctly if the patron comes
   * back to `events` mid-visit. `events` itself never touches this signal
   * (its resolver is `eventLandingResolver`/`opponentsResolver`, not
   * `EventDataResolver`), so this can only ever flip on because a
   * private-selling event was already reached via `seat-selection` in this
   * same visit. Still requires `isAuthenticated` so a guest never sees a
   * "Sign out" for a session that isn't theirs.
   */
  get showPrivateSellingActions(): boolean {
    return (
      this.landing &&
      this.isAuthenticated &&
      this.privateSellingService.isEnabled()
    );
  }
  /**
   * Gates the header band's background — checkout and summary have no
   * distinct header band; the band is painted the page's own grey instead.
   * Deliberately a separate getter from `showUserInfo`:
   * that flag is about whether the patron block renders, this one is about
   * background colour, and they happen to share the same route check today
   * but are conceptually different — keeping them separate avoids a future
   * change to one silently affecting the other.
   */
  get isCartRoute(): boolean {
    return this.actualPage === 'checkout' || this.actualPage === 'summary';
  }

  ngOnInit(): void {
    this.initComponent();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription: Subscription) =>
      subscription.unsubscribe()
    );
  }

  private initComponent() {
    const routerEventSubscription = this.router.events.subscribe(
      (event: Event) => {
        if (event instanceof NavigationEnd) {
          this.showLegend = event.url.includes('seat-selection');
          this.showTutorialButton =
            event.url.includes('seat-selection') ||
            event.url.includes('checkout');

          this.landing = event.url.includes('events');

          this.actualPage = this.resolveActivePage(event.urlAfterRedirects);
          this.backBtn =
            this.actualPage === 'checkout' ? 'checkout' : undefined;

          this.showUserInfo =
            this.actualPage === 'checkout' || this.actualPage === 'summary';

          // Re-arm the checkout_back guard (see goBack()) whenever checkout is (re-)entered —
          // a later transaction on this same long-lived header must still be able to fire it.
          if (this.actualPage === 'checkout') {
            this.hasTrackedCheckoutBack = false;
          }
        }
      }
    );

    const getEventSubscription = this.connectionService
      .getEvent()
      .subscribe(data => (this.event = data));

    /**
     * Checkout/summary never emit through `connectionService.getEvent()` above (that subject is
     * only fed by the seat-selection flow), so a refresh, OKTA return, or deep link straight into
     * `/checkout/:id` or `/summary/:id` left `event` unset and the header showed no event/date.
     * `transaction$` is populated immediately in both components' `ngOnInit` from the resolver's
     * payload, so it's a reliable source on direct loads too.
     */
    const transactionEventSubscription =
      this.transactionService.transaction$.subscribe(wrapper => {
        const event = wrapper?.transaction?.event;
        if (event) {
          this.event = event;
        }
      });

    /**
     * A persistent subscription rather than a one-shot check on route
     * change: the header stays mounted for the whole app (it isn't
     * recreated per page), and in demo mode signing in via checkout's
     * inline `app-okta-container` doesn't trigger any navigation/route
     * event for the header to react to. Subscribing to the live
     * OktaIntegrationService state instead — the same source
     * `app-okta-container` reads — picks that up immediately, on checkout
     * or summary, in demo mode and in a real Okta session alike.
     */
    const authStateSubscription = this.oktaIntegrationService
      .getIsAuthenticated()
      .subscribe({
        next: (isAuthenticated: boolean) =>
          void this.onAuthStateChange(isAuthenticated),
        error: (error: unknown) => console.error(error),
      });

    this.subscriptions.push(
      routerEventSubscription,
      getEventSubscription,
      transactionEventSubscription,
      authStateSubscription
    );
  }

  private async onAuthStateChange(isAuthenticated: boolean): Promise<void> {
    this.isAuthenticated = isAuthenticated;
    if (!isAuthenticated) {
      this.patronName = undefined;
      this.patronEmail = undefined;
      return;
    }

    const patron = await this.oktaIntegrationService.getUserInfo();
    this.patronName = patron?.name ?? 'Patron name not found';
    // No fallback string here (unlike patronName above): an absent email is
    // simply not rendered (see the `@if (patronEmail)` in the template)
    // rather than shown as fabricated text pretending to be an address.
    this.patronEmail = patron?.email;
  }

  signOut(): void {
    // `isAuthenticated`/`patronName` reset themselves via the persistent
    // getIsAuthenticated() subscription above once the service flips its
    // state — no need to duplicate that here.
    this.oktaIntegrationService.signOut().subscribe({
      error: (error: unknown) => console.error(error),
    });
  }

  /**
   * Starts the Okta redirect login — the entry point that replaced the
   * checkout card's inline `app-okta-container` login link.
   * `window.location.href` as the origin brings the
   * patron back to the page they were on.
   */
  signIn(): void {
    this.oktaIntegrationService
      .signInWithRedirect(window.location.href, 'okta/redirect')
      .subscribe({ error: (error: unknown) => console.error(error) });
  }

  // Guards trackCheckoutBack() below against a double-click on the Back button firing
  // checkout_back twice before restartTransaction()'s navigation lands — re-armed whenever
  // checkout is (re-)entered, see initComponent()'s ActivationEnd handler above.
  private hasTrackedCheckoutBack = false;

  // Only ever called for `backBtn === 'checkout'` (see the template) —
  // 'summary' has no back button, so there's no other page to branch on here.
  // This is the real "Back" button on checkout: it calls TransactionService.restartTransaction()
  // directly rather than through CheckoutComponent, so checkout_back must be tracked from here —
  // see AnalyticsService.trackCheckoutBack's doc comment.
  goBack() {
    this.trackCheckoutBack();
    this.transactionService.restartTransaction();
  }

  private trackCheckoutBack(): void {
    if (this.hasTrackedCheckoutBack) {
      return;
    }
    const transaction = this.transactionService.transaction$.value?.transaction;
    if (!transaction) {
      return;
    }
    this.hasTrackedCheckoutBack = true;

    const gameId = transaction.event?.pvEventId;
    this.analyticsService.trackCheckoutBack({
      transactionId: transaction.id.toString(),
      gameId: gameId !== undefined ? String(gameId) : undefined,
      value: transaction.tdcTransaction?.balance,
      currency: 'USD',
    });
  }

  /**
   * The page name the header keys its Back button / Sign-in pill / cart colours on, resolved
   * as the first URL segment that is a *known route* — the same KNOWN_ROUTE_SEGMENTS list
   * groupLinkGuard() uses to tell a sales-rep slug apart from a real route (which is exactly
   * why a `/{slug}/checkout/:id` prefix can no longer shadow the page: the slug is never in
   * the list). Unrecognised urls resolve to undefined, like the old url[0].path did.
   */
  private resolveActivePage(url: string): string | undefined {
    const segments = url.split('?')[0].split('/').filter(Boolean);
    return segments.find(segment => KNOWN_ROUTE_SEGMENTS.includes(segment));
  }

  openLegend() {
    const { phone, email } = this.configurationService.configuration;
    const modalData = {
      title: 'Location Legend',
      content: buildLegendContent({
        isTopView: this.isTopview,
        phone,
        email,
      }),
      acceptBtnName: 'CLOSE',
      compact: true,
    };
    this.modalsService.openModal(modalData);
  }

  // Tutorial
  openTutorialQuestion() {
    this.tutorialService.openTutorial();
  }

  isNotFoundRoute() {
    return this.router.url === '/not-found';
  }
}
